// EmailComposerContext — global mount point for the inline email
// composer so any descendant of PageLayout can pop it open without
// prop drilling.
//
// CONSUMER: internal-ui (web)
//
// Why: Ask Stew (PR #5) needs to hand off an LLM-drafted email to
// the existing EmailComposer with pre-fill values (to / subject /
// body / household + contact context). Per the V1 plan: reuse the
// existing composer rather than build a parallel send path — one
// send path, one activity-logging path, one signature loader, one
// AiHelpPanel.
//
// Per-page `ComposeButton` + `EmailSidebarSection` still mount
// their own EmailComposer locally — those are pre-existing and not
// in PR #5's scope. Both surfaces working in parallel is fine; only
// one EmailComposer is ever visible at a time (each opens its own
// drawer instance, mounted only when `open=true`).

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  EmailComposer,
  type EmailComposerRecordContext,
} from "../components/email/EmailComposer";

// What the consumer passes to openWith(). Mirrors the subset of
// EmailComposerProps that Stew's Email Draft card cares about.
export interface OpenEmailComposerInput {
  /** At least one of householdId / contactId is required. */
  householdId?: string | null;
  contactId?: string | null;
  /**
   * The Processing / Opportunity / service record this compose belongs
   * to. Give it whenever the composer is opened from a record page: it
   * keeps that record's half-written drafts off other records in the
   * same household, and it puts that record's own documents one click
   * away in the Attach menu.
   */
  recordContext?: EmailComposerRecordContext | null;
  prefillTo?: string[];
  prefillSubject?: string;
  prefillBodyHtml?: string;
  // Reply-thread context (Outlook keeps threads together when
  // In-Reply-To is set).
  replyToMessageId?: string | null;
  replyToConversationId?: string | null;
  replyToSubject?: string | null;
  replyToBodyPreview?: string | null;
}

export interface EmailComposerApi {
  /** Open the global composer with the provided pre-fills. */
  openWith: (input: OpenEmailComposerInput) => void;
  /** Programmatic close (rare — the composer's X handles the normal path). */
  close: () => void;
  /** Whether the global composer is currently mounted as visible. */
  isOpen: boolean;
}

/** Exported so a test or a story can supply the API without standing up
 *  the real composer, which needs a session. Application code uses
 *  EmailComposerProvider — this is the seam, not a second provider. */
export const EmailComposerContext = createContext<EmailComposerApi | null>(null);

interface InternalState extends OpenEmailComposerInput {
  open: boolean;
}

const EMPTY_STATE: InternalState = { open: false };

export function EmailComposerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<InternalState>(EMPTY_STATE);

  const openWith = useCallback((input: OpenEmailComposerInput) => {
    // Validate the same way ComposeButton's call site does — at least
    // one of householdId / contactId is required. Logged as a console
    // warning rather than thrown so a misbehaving caller doesn't
    // crash the panel.
    if (
      (input.householdId === undefined || input.householdId === null) &&
      (input.contactId === undefined || input.contactId === null)
    ) {
      console.warn(
        "EmailComposerContext.openWith: householdId or contactId required",
      );
      return;
    }
    setState({ ...input, open: true });
  }, []);

  const close = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  const api = useMemo<EmailComposerApi>(
    () => ({ openWith, close, isOpen: state.open }),
    [openWith, close, state.open],
  );

  // The composer itself mounts here so it's available on every
  // authenticated route. Pass `open` from state. When closed, the
  // EmailComposer component itself returns null (its Drawer wrapper
  // is gated on `open`), so the DOM stays tidy.
  return (
    <EmailComposerContext.Provider value={api}>
      {children}
      {/* Conditional props: only spread the keys the user provided
          so EmailComposer's exactOptionalPropertyTypes guards don't
          fight us with explicit-undefined. */}
      <EmailComposer
        open={state.open}
        onClose={close}
        {...(state.householdId !== undefined && state.householdId !== null
          ? { householdId: state.householdId }
          : {})}
        {...(state.contactId !== undefined && state.contactId !== null
          ? { contactId: state.contactId }
          : {})}
        {...(state.recordContext !== undefined && state.recordContext !== null
          ? { recordContext: state.recordContext }
          : {})}
        {...(state.prefillTo ? { prefillTo: state.prefillTo } : {})}
        {...(state.prefillSubject !== undefined
          ? { prefillSubject: state.prefillSubject }
          : {})}
        {...(state.prefillBodyHtml !== undefined
          ? { prefillBodyHtml: state.prefillBodyHtml }
          : {})}
        {...(state.replyToMessageId !== undefined
          ? { replyToMessageId: state.replyToMessageId }
          : {})}
        {...(state.replyToConversationId !== undefined
          ? { replyToConversationId: state.replyToConversationId }
          : {})}
        {...(state.replyToSubject !== undefined
          ? { replyToSubject: state.replyToSubject }
          : {})}
        {...(state.replyToBodyPreview !== undefined
          ? { replyToBodyPreview: state.replyToBodyPreview }
          : {})}
        onSent={close}
      />
    </EmailComposerContext.Provider>
  );
}

export function useEmailComposer(): EmailComposerApi {
  const ctx = useContext(EmailComposerContext);
  if (!ctx) {
    throw new Error(
      "useEmailComposer must be called inside an <EmailComposerProvider> tree.",
    );
  }
  return ctx;
}
